(function () {
  const PRODUCT_PARAM = "products";
  const PRODUCT_CARD_SECTION_ID = "adp-product-card";
  const PREVIEW_PARAMS = ["preview_theme_id", "_ab", "_fd", "_sc", "pb"];
  const productCardCache = new Map();
  let isApplying = false;
  let observer = null;
  let observerTimer = null;

  function escapeSelector(value) {
    if (window.CSS?.escape) {
      return CSS.escape(value);
    }

    return value.replace(/["\\]/g, "\\$&");
  }

  function getAdpHandles() {
    const rawProducts = new URLSearchParams(window.location.search).get(PRODUCT_PARAM);

    if (!rawProducts) {
      return [];
    }

    return rawProducts
      .split(",")
      .map((handle) => handle.trim().toLowerCase())
      .filter((handle) => /^[a-z0-9][a-z0-9-]*$/.test(handle))
      .filter((handle, index, handles) => handles.indexOf(handle) === index);
  }

  function copyPreviewParams(toUrl, fromUrl = new URL(window.location.href)) {
    PREVIEW_PARAMS.forEach((paramName) => {
      const paramValue = fromUrl.searchParams.get(paramName);

      if (paramValue !== null && !toUrl.searchParams.has(paramName)) {
        toUrl.searchParams.set(paramName, paramValue);
      }
    });
  }

  function getProductGrid() {
    return document.getElementById("items-grid");
  }

  function getProductItems(productGrid) {
    return Array.from(productGrid?.children || []).filter((item) => item.matches("[data-adp-product-card][data-adp-handle]"));
  }

  function findProductItem(productGrid, handle) {
    return productGrid?.querySelector(`[data-adp-product-card][data-adp-handle="${escapeSelector(handle)}"]`) || null;
  }

  async function fetchSection(url) {
    const response = await fetch(url.toString(), {
      headers: {
        "X-Requested-With": "XMLHttpRequest"
      }
    });

    if (!response.ok) {
      throw new Error(`ADP section request failed: ${response.status}`);
    }

    return new DOMParser().parseFromString(await response.text(), "text/html");
  }

  function rememberPageSize(productGrid) {
    if (!productGrid.dataset.adpPageSize) {
      productGrid.dataset.adpPageSize = String(getProductItems(productGrid).length);
    }
  }

  async function fetchProductItem(handle) {
    if (productCardCache.has(handle)) {
      return productCardCache.get(handle).cloneNode(true);
    }

    try {
      const root = window.Shopify?.routes?.root || "/";
      const productUrl = new URL(`${root}products/${encodeURIComponent(handle)}`, window.location.origin);
      copyPreviewParams(productUrl);
      productUrl.searchParams.set("section_id", PRODUCT_CARD_SECTION_ID);

      const productDocument = await fetchSection(productUrl);
      const item = productDocument.querySelector(`[data-adp-product-card][data-adp-handle="${escapeSelector(handle)}"]`);

      if (!item) {
        console.warn(`[ADP] Product card was not found for handle: ${handle}`);
        return null;
      }

      productCardCache.set(handle, item);
      return item.cloneNode(true);
    } catch (error) {
      console.warn(`[ADP] Unable to fetch product card for handle: ${handle}`, error);
      return null;
    }
  }

  async function getPromotedItems(productGrid, handles) {
    const promotedItems = [];

    for (const handle of handles) {
      let item = findProductItem(productGrid, handle);

      if (!item) {
        item = await fetchProductItem(handle);
      }

      if (!item) {
        continue;
      }

      item.dataset.adpPromoted = "true";
      promotedItems.push(item);
    }

    return promotedItems;
  }

  function removeDuplicatePromotedItems(productGrid, promotedItems) {
    const promotedHandles = new Set(promotedItems.map((item) => item.dataset.adpHandle));

    getProductItems(productGrid).forEach((item) => {
      if (promotedHandles.has(item.dataset.adpHandle) && !promotedItems.includes(item)) {
        item.remove();
      }
    });
  }

  function trimToPageSize(productGrid) {
    const pageSize = Number(productGrid.dataset.adpPageSize);

    if (!pageSize) {
      return;
    }

    while (getProductItems(productGrid).length > pageSize) {
      const removableItems = getProductItems(productGrid).filter((item) => item.dataset.adpPromoted !== "true");
      const itemToRemove = removableItems[removableItems.length - 1];

      if (!itemToRemove) {
        break;
      }

      itemToRemove.remove();
    }
  }

  function initNewItems(items) {
    if (!window.Alpine?.initTree) {
      return;
    }

    items.forEach((item) => {
      if (!item.isConnected) {
        return;
      }

      window.Alpine.initTree(item);
    });
  }

  function preserveAdpInputsAndLinks() {
    const handles = getAdpHandles();

    if (!handles.length) {
      return;
    }

    const value = handles.join(",");

    document.querySelectorAll("form").forEach((form) => {
      let input = form.querySelector(`input[name="${PRODUCT_PARAM}"]`);

      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = PRODUCT_PARAM;
        form.append(input);
      }

      input.value = value;
    });

    document.querySelectorAll('a[href*="?"]').forEach((link) => {
      try {
        const url = new URL(link.href);

        if (url.origin === window.location.origin && !url.searchParams.has(PRODUCT_PARAM)) {
          copyPreviewParams(url);
          url.searchParams.set(PRODUCT_PARAM, value);
          link.href = url.toString();
        }
      } catch (error) {
      }
    });
  }

  async function applyAdp() {
    const handles = getAdpHandles();

    if (!handles.length || isApplying) {
      return;
    }

    isApplying = true;

    try {
      const productGrid = getProductGrid();

      if (!productGrid) {
        preserveAdpInputsAndLinks();
        return;
      }

      rememberPageSize(productGrid);
      preserveAdpInputsAndLinks();

      const promotedItems = await getPromotedItems(productGrid, handles);

      if (!promotedItems.length) {
        return;
      }

      const currentItems = getProductItems(productGrid);
      const alreadyPromoted = promotedItems.every((item, index) => currentItems[index] === item);

      if (alreadyPromoted) {
        trimToPageSize(productGrid);
        preserveAdpInputsAndLinks();
        return;
      }

      removeDuplicatePromotedItems(productGrid, promotedItems);
      productGrid.prepend(...promotedItems);
      trimToPageSize(productGrid);
      initNewItems(promotedItems);
      preserveAdpInputsAndLinks();
    } catch (error) {
      console.warn("[ADP] Unable to apply product ordering", error);
    } finally {
      isApplying = false;
    }
  }

  function scheduleApplyAdp() {
    window.clearTimeout(observerTimer);
    observerTimer = window.setTimeout(applyAdp, 100);
  }

  function initObserver() {
    const container = document.getElementById("ProductGridContainer") || document.body;

    if (observer) {
      observer.disconnect();
    }

    observer = new MutationObserver(scheduleApplyAdp);
    observer.observe(container, { childList: true, subtree: true });
  }

  function initAdp() {
    initObserver();
    applyAdp();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAdp);
  } else {
    initAdp();
  }

  window.addEventListener("pageshow", applyAdp);
  window.addEventListener("popstate", applyAdp);
})();